import React, { useEffect, useState, useCallback } from "react";
import { Server, Database, Mail, Clock, RefreshCw, Activity } from "lucide-react";
import { getHealth } from "../../api";

const STATUS_STYLE = {
  ok: { dot: "bg-green-500", text: "text-green-600", label: "正常" },
  running: { dot: "bg-green-500", text: "text-green-600", label: "運行中" },
  down: { dot: "bg-red-500", text: "text-red-600", label: "異常" },
  stopped: { dot: "bg-red-500", text: "text-red-600", label: "已停止" },
  not_configured: { dot: "bg-amber-400", text: "text-amber-600", label: "未設定" },
  unknown: { dot: "bg-gray-400", text: "text-gray-500", label: "未知" },
};

function latencyColor(ms) {
  if (ms == null) return "text-muted-foreground";
  if (ms < 300) return "text-green-600";
  if (ms < 800) return "text-amber-600";
  return "text-red-600";
}

function ServiceChip({ icon: Icon, name, svc, extra }) {
  const s = STATUS_STYLE[svc?.status] || STATUS_STYLE.unknown;
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border bg-white px-4 py-3 shadow-sm">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-orange-50 text-brand">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-bold">{name}</span>
          <span className={`h-2 w-2 rounded-full ${s.dot}`} />
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className={`font-medium ${s.text}`}>{s.label}</span>
          {svc?.latencyMs != null && (
            <span className={`font-mono ${latencyColor(svc.latencyMs)}`}>{svc.latencyMs} ms</span>
          )}
          {extra && <span className="truncate text-muted-foreground">{extra}</span>}
        </div>
      </div>
    </div>
  );
}

export default function HealthPanel() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getHealth()
      .then((h) => { setHealth(h); setError(false); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000); // 每 15 秒自動刷新
    return () => clearInterval(t);
  }, [load]);

  const nextNotify =
    health?.scheduler?.jobs?.length
      ? `下次 ${health.scheduler.jobs[0].next}`
      : undefined;

  return (
    <div className="rounded-xl border border-border bg-[#FAFAFA] p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 text-sm font-bold">
          <Activity className="h-4 w-4 text-brand" /> 系統狀態
          {health?.time && (
            <span className="ml-1 text-xs font-normal text-muted-foreground">
              更新於 {health.time}
            </span>
          )}
        </h3>
        <button
          onClick={load}
          className="flex items-center gap-1 rounded-lg border border-border bg-white px-2.5 py-1 text-xs font-medium hover:bg-secondary"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> 刷新
        </button>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
          無法連線到伺服器，請稍後再試或檢查後端服務。
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <ServiceChip icon={Server} name="API 伺服器" svc={health?.api} />
          <ServiceChip icon={Database} name="資料庫" svc={health?.database} />
          <ServiceChip icon={Mail} name="Email 寄信" svc={health?.email} />
          <ServiceChip icon={Clock} name="排程通知" svc={health?.scheduler} extra={nextNotify} />
        </div>
      )}
    </div>
  );
}
